import type { Markets } from '~api';
import {
  formatCompactCurrency,
  formatCompactNumber,
  formatDelta,
  formatPercent,
  NO_FIGURE,
} from '~lib';
import {
  ANNUAL_GGR_VIEW_LABELS,
  type AnnualGgrView,
  type Region,
  type RegulatoryStatus,
  type SeriesPoint,
} from '~portal/data';
import type { FragmentContent } from '~portal/lib/SsrComponent';

/**
 * View models for the Regions pages (UI round 2, Lane E): the warehouse's `markets.*` rows,
 * joined with the fixture regions only for what the warehouse does not supply (descriptions,
 * regulatory status, the fixture-only sections), shaped for the directory cards and rows (D-3)
 * and the region page (D-12). Pure — no hooks, no rendering — so every rule is unit-tested.
 *
 * What is live and what is a fixture is carried on the model (`illustrative` flags), so the page
 * badges exactly the fixture-derived parts and nothing live.
 */

export type DirectoryKind = Markets.Kind | 'continent';

/** @desc One directory card or row (Figma `385:7478` / `388:55000`). */
export type DirectoryEntry = {
  slug: string;
  name: string;
  kind: DirectoryKind;
  /** @desc The continent eyebrow; "Continent" on a continent's own card. */
  eyebrow: string;
  continentSlug: string | null;
  /** @desc `EntityLogo` flag code (`gb`, `us-nj`); absent on roll-ups and continents. */
  flagCode?: string;
  /** @desc The letters the tile falls back to (`GB`, `NJ`, `EU+`). */
  code?: string;
  description: string;
  /** @desc The description is fixture copy (an overview's first sentence), not generated from live figures. */
  descriptionFromFixture: boolean;
  updatedAt: Date | null;
  onlineGgrUsd: number | null;
  ggrYear: number | null;
  yoyGrowth: number | null;
  /** @desc From the fixture where it has the region (R9 deviation); illustrative. */
  regulatoryStatus?: RegulatoryStatus;
  /** @desc Continents: how many markets the warehouse holds for it. */
  marketCount?: number;
  /**
   * @desc Portal group slugs present in the market: live (`operatorsPresent` on the warehouse row,
   * where licensed-entity rows exist) or else the fixture's, badged. Absent when neither knows.
   */
  operatorsPresent?: readonly string[];
  /** @desc `operatorsPresent` came from the warehouse, not the fixture. */
  operatorsLive?: boolean;
};

const usd = (value: number | null | undefined) => formatCompactCurrency(value, 'USD');

/** @desc A missing figure on the region page reads "n/a", not a dash that could pass for zero. */
export const orNa = (formatted: string): string => (formatted === NO_FIGURE ? 'n/a' : formatted);

/** @desc A fixture region's one-line description: the first sentence of its overview. */
export const fixtureDescription = (region: Region): string => {
  const [first] = region.overview.split(/(?<=[.!?])\s+/);
  return (first ?? region.overview).trim();
};

/** @desc ISO code → `EntityLogo` flag code: `GB` → `gb`, `US-NJ` → `us-nj`. */
export const flagCodeOf = (isoCode: string | undefined): string | undefined =>
  isoCode ? isoCode.toLowerCase() : undefined;

/** @desc ISO code → the letters tile: a country's code, a state's subdivision (`US-NJ` → `NJ`). */
export const tileCodeOf = (isoCode: string | undefined): string | undefined => {
  if (!isoCode) return undefined;
  const [, subdivision] = isoCode.split('-');
  return (subdivision ?? isoCode).toUpperCase();
};

const ggrClause = (ggr: number | null, year: number | null) =>
  ggr === null ? '' : ` · online GGR ${usd(ggr)}${year ? ` (${year})` : ''}`;

/**
 * @desc The line a market without fixture copy gets: "Japan, Asia Pacific · online GGR $1.2bn
 * (2025)". Every figure in it is live.
 */
export const generatedDescription = (listing: Markets.Listing): string => {
  const ggr = ggrClause(listing.onlineGgrUsd, listing.ggrYear);
  if (listing.kind === 'roll-up') {
    return `Markets in ${listing.continent.name} not tracked one by one${ggr}`;
  }
  return `${listing.name}, ${listing.continent.name}${ggr}`;
};

/** @desc A warehouse row as a directory entry, with the fixture's copy and status where it has the market. */
export const toDirectoryEntry = (listing: Markets.Listing, fixture?: Region): DirectoryEntry => {
  const regulatoryStatus = listing.regulatoryStatus ?? fixture?.regulatoryStatus;
  const flagCode = flagCodeOf(listing.isoCode);
  const code = tileCodeOf(listing.isoCode) ?? fixture?.code;
  return {
    slug: listing.slug,
    name: listing.name,
    kind: listing.kind,
    eyebrow: listing.continent.name,
    continentSlug: listing.continent.slug,
    ...(flagCode ? { flagCode } : {}),
    ...(code ? { code } : {}),
    description: fixture ? fixtureDescription(fixture) : generatedDescription(listing),
    descriptionFromFixture: fixture !== undefined,
    updatedAt: listing.updatedAt ?? null,
    onlineGgrUsd: listing.onlineGgrUsd,
    ggrYear: listing.ggrYear,
    yoyGrowth: listing.yoyGrowth,
    ...(regulatoryStatus ? { regulatoryStatus } : {}),
    ...(listing.operatorsPresent
      ? { operatorsPresent: listing.operatorsPresent, operatorsLive: true }
      : fixture
        ? { operatorsPresent: fixture.operatorsPresent, operatorsLive: false }
        : {}),
  };
};

const bySlug = (fixtures: readonly Region[]) => new Map(fixtures.map((r) => [r.slug, r]));

/** @desc Every warehouse row as a directory entry (the "By market" tab). */
export const marketEntries = (
  listings: readonly Markets.Listing[],
  fixtures: readonly Region[],
): DirectoryEntry[] => {
  const fixtureBySlug = bySlug(fixtures);
  return listings.map((listing) => toDirectoryEntry(listing, fixtureBySlug.get(listing.slug)));
};

const latest = (dates: ReadonlyArray<Date | undefined>): Date | null =>
  dates.reduce<Date | null>((best, at) => (at && (best === null || at > best) ? at : best), null);

/**
 * @desc The continents as roll-up entries (the "By continent" tab), grouped from each row's
 * `continent` facet. GGR is the sum over countries and roll-ups only: a US or Australian state is
 * already inside its country's figure, so adding states would count those markets twice. `null`
 * when no row in the continent has a figure. Copy comes from the fixture continent where it has
 * one; the card links to the continent page (`markets.continent`, the live aggregate).
 */
export const continentEntries = (
  listings: readonly Markets.Listing[],
  fixtures: readonly Region[],
): DirectoryEntry[] => {
  const fixtureBySlug = bySlug(fixtures);
  const groups = new Map<string, { continent: Markets.Continent; rows: Markets.Listing[] }>();
  for (const listing of listings) {
    const group = groups.get(listing.continent.slug);
    if (group) group.rows.push(listing);
    else groups.set(listing.continent.slug, { continent: listing.continent, rows: [listing] });
  }
  return [...groups.values()].map(({ continent, rows }) => {
    const counted = rows.filter((row) => row.kind !== 'state' && row.onlineGgrUsd !== null);
    const ggr = counted.length
      ? counted.reduce((sum, row) => sum + (row.onlineGgrUsd ?? 0), 0)
      : null;
    const years = rows.map((row) => row.ggrYear).filter((y): y is number => y !== null);
    const year = years.length ? Math.max(...years) : null;
    const fixture = fixtureBySlug.get(continent.slug);
    const marketWord = rows.length === 1 ? 'market' : 'markets';
    return {
      slug: continent.slug,
      name: continent.name,
      kind: 'continent' as const,
      eyebrow: 'Continent',
      continentSlug: null,
      ...(fixture ? { code: fixture.code } : {}),
      description: fixture
        ? fixtureDescription(fixture)
        : `${rows.length} ${marketWord}${ggrClause(ggr, year)}`,
      descriptionFromFixture: fixture !== undefined,
      updatedAt: latest(rows.map((row) => row.updatedAt)),
      onlineGgrUsd: ggr,
      ggrYear: year,
      yoyGrowth: null,
      marketCount: rows.length,
    };
  });
};

// ---------------------------------------------------------------------------------------------
// Region page
// ---------------------------------------------------------------------------------------------

export type KpiKey = 'ggr' | 'forecast' | 'per-adult' | 'adults';

export type RegionKpiItem = {
  key: KpiKey;
  label: string;
  value: string;
  delta?: number;
  hint?: string;
};

export type RegionPageKpis = { year: number | null; items: RegionKpiItem[]; illustrative: boolean };

export type RegionPageMacro = {
  year: number | null;
  pairs: Array<{ label: string; value: string }>;
  illustrative: boolean;
};

/** @desc The region chart's views: the fixture's three plus the live-only "By sport" (lane 2). */
export type RegionAnnualView = AnnualGgrView | 'by-sport';

export const BY_SPORT_LABEL = 'By sport';

type RegionAnnualSegment = { key: string; label: string; points: SeriesPoint[] };

export type RegionAnnualSet = {
  view: RegionAnnualView;
  label: string;
  forecastFrom: number | null;
  /** @desc What the chart draws. */
  segments: RegionAnnualSegment[];
  /** @desc What the table twin lists, where it differs from the chart (every sport on "By sport"). */
  tableSegments?: RegionAnnualSegment[];
};

/** @desc How many sports the "By sport" chart draws by name; the rest fold into one segment. */
export const BY_SPORT_TOP_N = 6;

/** @desc The folded segment on the "By sport" chart. */
export const OTHER_SPORTS = { key: 'other-sports', label: 'All other sports' } as const;

/** @desc Everything the region page draws, with the live/fixture split explicit. */
export type RegionPage = {
  slug: string;
  name: string;
  kind: DirectoryKind;
  /** @desc The continent eyebrow; "Continent" on a continent page. */
  eyebrow: string | null;
  flagCode?: string;
  code?: string;
  description: string | null;
  updatedAt: Date | null;
  /** @desc The masthead figures, macro band and annual series came from the warehouse. */
  live: boolean;
  kpis: RegionPageKpis;
  macro: RegionPageMacro;
  annual: RegionAnnualSet[];
  /** @desc Live licensees with online GGR (Lane M); absent where the warehouse has no rows. */
  operatorsPresent?: Markets.OperatorsPresent;
  /** @desc Live licensed-entity market share (Lane M); absent where the warehouse has no rows. */
  marketShare?: Markets.MarketShare;
  /** @desc Live monthly online GGR and turnover (lane 3); absent where the warehouse has no rows. */
  monthly?: Markets.Monthly;
  /** @desc The fixture region, for the fixture-only sections (badged); absent for a live-only market. */
  fixture?: Region;
  /** @desc Continent pages: the member markets, live first, then fixture-only ones (badged). */
  members?: ContinentMember[];
  /** @desc Continent pages: the members' states, live, already inside their country's figure. */
  memberStates?: ContinentMember[];
};

/** @desc One market on a continent page: a live warehouse member, or a fixture-only one (badged). */
export type ContinentMember = {
  slug: string;
  name: string;
  flagCode?: string;
  code?: string;
  onlineGgrUsd: number | null;
  /** @desc `false`: the fixture lists it but the warehouse does not hold it (`ontario`); badged. */
  live: boolean;
};

/** @desc The headline online-GGR definition (decision 17), stated on every continent page. */
export const ONLINE_GGR_DEFINITION =
  'Online GGR: all online products, onshore and offshore, lottery included.';

/** @desc How a continent's figures are built, said beside the definition. */
export const continentBasisText = (memberCount: number): string =>
  `Continent figures sum its ${memberCount} ${memberCount === 1 ? 'country or roll-up' : 'countries and roll-ups'} (a state is inside its country) in US dollars, so growth is in US dollars too; inflation and GDP growth do not add up across economies.`;

const ANNUAL_VIEWS: readonly AnnualGgrView[] = [
  'by-product',
  'online-vs-land',
  'onshore-vs-offshore',
];

const yearLabel = (year: number, forecast: boolean) => `${year}${forecast ? 'e' : ''}`;

/** @desc The live annual series as chart sets: one per tab the warehouse can serve. */
export const liveAnnualSets = (annual: Markets.AnnualGgr): RegionAnnualSet[] =>
  ANNUAL_VIEWS.flatMap((view) => {
    const set = annual[view];
    if (!set || set.segments.length === 0) return [];
    return [
      {
        view,
        label: ANNUAL_GGR_VIEW_LABELS[view],
        forecastFrom: set.forecastFrom,
        segments: set.segments.map((segment) => ({
          key: segment.key,
          label: segment.label,
          points: segment.points.map((p) => ({
            label: yearLabel(p.year, p.forecast),
            value: p.value,
            forecast: p.forecast,
          })),
        })),
      },
    ];
  });

const fixtureAnnualSets = (region: Region): RegionAnnualSet[] =>
  ANNUAL_VIEWS.flatMap((view) => {
    const set = region.annualGgr[view];
    return set.segments.length === 0
      ? []
      : [
          {
            view,
            label: ANNUAL_GGR_VIEW_LABELS[view],
            forecastFrom: set.forecastFrom,
            segments: set.segments,
          },
        ];
  });

type KpiInput = {
  year: number | null;
  local: { value: number; currency: string } | null;
  ggrUsd: number | null;
  yoy: number | null;
  forecastUsd: number | null;
  forecastYear: number | null;
  perAdultUsd: number | null;
  adults: number | null;
};

const kpiItems = (k: KpiInput): RegionKpiItem[] => {
  const showsLocal = k.local !== null && k.local.currency !== 'USD';
  const headline = k.local ? formatCompactCurrency(k.local.value, k.local.currency) : usd(k.ggrUsd);
  return [
    {
      key: 'ggr',
      label: 'Total online GGR',
      value: orNa(headline),
      ...(k.yoy !== null ? { delta: k.yoy } : {}),
      ...(showsLocal && k.ggrUsd !== null ? { hint: `${usd(k.ggrUsd)} in US dollars` } : {}),
    },
    {
      key: 'forecast',
      label: k.forecastYear ? `Forecast GGR ${k.forecastYear}` : 'Forecast GGR',
      value: orNa(usd(k.forecastUsd)),
    },
    {
      key: 'per-adult',
      label: 'GGR per adult',
      value: orNa(formatCompactCurrency(k.perAdultUsd, 'USD', 0)),
    },
    { key: 'adults', label: 'Adult population', value: orNa(formatCompactNumber(k.adults)) },
  ];
};

const macroPairs = (m: {
  onlineShareOfTotal: number | null;
  gdpUsd: number | null;
  inflation: number | null;
  gdpGrowth: number | null;
}) => [
  { label: 'Online share of total GGR', value: orNa(formatPercent(m.onlineShareOfTotal)) },
  { label: 'GDP', value: orNa(usd(m.gdpUsd)) },
  { label: 'Inflation', value: orNa(formatPercent(m.inflation)) },
  { label: 'GDP growth', value: orNa(formatDelta(m.gdpGrowth)) },
];

/**
 * @desc The live by-sport set as the chart's "By sport" view; absent without rows. The server ranks
 * the sports (latest actual year, ties by name); the chart draws the first {@link BY_SPORT_TOP_N}
 * by name and folds the rest into {@link OTHER_SPORTS}, and the table lists every sport.
 */
export const bySportAnnualSet = (set: Markets.AnnualGgrSet | undefined): RegionAnnualSet[] => {
  if (!set || set.segments.length === 0) return [];
  const all = set.segments.map((segment) => ({
    key: segment.key,
    label: segment.label,
    points: segment.points.map((p) => ({
      label: yearLabel(p.year, p.forecast),
      value: p.value,
      forecast: p.forecast,
    })),
  }));
  const named = all.slice(0, BY_SPORT_TOP_N);
  const rest = all.slice(BY_SPORT_TOP_N);
  const folded = rest[0]
    ? [
        {
          ...OTHER_SPORTS,
          points: rest[0].points.map((point, i) => ({
            ...point,
            value: rest.reduce((sum, segment) => sum + (segment.points[i]?.value ?? 0), 0),
          })),
        },
      ]
    : [];
  return [
    {
      view: 'by-sport',
      label: BY_SPORT_LABEL,
      forecastFrom: set.forecastFrom,
      segments: [...named, ...folded],
      ...(rest.length > 0 ? { tableSegments: all } : {}),
    },
  ];
};

/** @desc A market page from the warehouse, with the fixture (if any) for its fixture-only sections. */
export const liveRegionPage = (detail: Markets.Detail, fixture?: Region): RegionPage => {
  const { listing, kpis, macro } = detail;
  const flagCode = flagCodeOf(listing.isoCode);
  const code = tileCodeOf(listing.isoCode) ?? fixture?.code;
  return {
    slug: listing.slug,
    name: listing.name,
    kind: listing.kind,
    eyebrow: listing.continent.name,
    ...(flagCode ? { flagCode } : {}),
    ...(code ? { code } : {}),
    description: fixture ? fixtureDescription(fixture) : generatedDescription(listing),
    updatedAt: listing.updatedAt ?? null,
    live: true,
    kpis: {
      year: kpis.year,
      illustrative: false,
      items: kpiItems({
        year: kpis.year,
        local: kpis.onlineGgrLocal,
        ggrUsd: kpis.onlineGgrUsd,
        yoy: kpis.yoyGrowth,
        forecastUsd: kpis.forecastGgrUsd,
        forecastYear: kpis.forecastYear,
        perAdultUsd: kpis.ggrPerAdultUsd,
        adults: kpis.adultPopulation,
      }),
    },
    macro: { year: macro.year, illustrative: false, pairs: macroPairs(macro) },
    annual: [...liveAnnualSets(detail.annualGgr), ...bySportAnnualSet(detail.annualGgrBySport)],
    ...(detail.operatorsPresent ? { operatorsPresent: detail.operatorsPresent } : {}),
    ...(detail.marketShare ? { marketShare: detail.marketShare } : {}),
    ...(detail.monthly ? { monthly: detail.monthly } : {}),
    ...(fixture ? { fixture } : {}),
  };
};

const liveMember = (listing: Markets.Listing): ContinentMember => {
  const flagCode = flagCodeOf(listing.isoCode);
  const code = tileCodeOf(listing.isoCode);
  return {
    slug: listing.slug,
    name: listing.name,
    ...(flagCode ? { flagCode } : {}),
    ...(code ? { code } : {}),
    onlineGgrUsd: listing.onlineGgrUsd,
    live: true,
  };
};

/**
 * @desc A continent page from its live aggregate (round 3 lane 4): masthead, KPIs, macro band and
 * annual series over the member markets, and the members themselves. The fixture continent gives
 * only its description and the members the warehouse does not hold (`ontario`, `alberta`), which
 * are listed after the live ones and badged; its figure sections are not drawn.
 */
export const continentRegionPage = (
  detail: Markets.ContinentDetail,
  fixture: Region | undefined,
  regions: readonly Region[],
): RegionPage => {
  const { continent, kpis, macro } = detail;
  const liveSlugs = new Set([...detail.members, ...detail.states].map((m) => m.slug));
  const fixtureOnly = (fixture?.childMarkets ?? [])
    .filter((slug) => !liveSlugs.has(slug))
    .flatMap((slug) => {
      const region = regions.find((r) => r.slug === slug);
      return region
        ? [
            {
              slug: region.slug,
              name: region.name,
              code: region.code,
              onlineGgrUsd: region.kpis.onlineGgrUsd,
              live: false,
            },
          ]
        : [];
    });
  return {
    slug: continent.slug,
    name: continent.name,
    kind: 'continent',
    eyebrow: 'Continent',
    ...(fixture ? { code: fixture.code } : {}),
    description: fixture
      ? fixtureDescription(fixture)
      : `${detail.members.length} markets${ggrClause(kpis.onlineGgrUsd, kpis.year)}`,
    updatedAt: detail.updatedAt ?? null,
    live: true,
    kpis: {
      year: kpis.year,
      illustrative: false,
      items: kpiItems({
        year: kpis.year,
        local: kpis.onlineGgrLocal,
        ggrUsd: kpis.onlineGgrUsd,
        yoy: kpis.yoyGrowth,
        forecastUsd: kpis.forecastGgrUsd,
        forecastYear: kpis.forecastYear,
        perAdultUsd: kpis.ggrPerAdultUsd,
        adults: kpis.adultPopulation,
      }),
    },
    macro: { year: macro.year, illustrative: false, pairs: macroPairs(macro) },
    annual: liveAnnualSets(detail.annualGgr),
    members: [...detail.members.map(liveMember), ...fixtureOnly],
    memberStates: detail.states.map(liveMember),
  };
};

/**
 * @desc A page the warehouse cannot serve — a fixture market it does not hold (`ontario`,
 * `alberta`) — drawn from the fixture alone, every figure illustrative.
 */
export const fixtureRegionPage = (region: Region, regions: readonly Region[]): RegionPage => {
  const continent = regions.find((r) => r.slug === region.continentSlug);
  const { kpis, macro } = region;
  return {
    slug: region.slug,
    name: region.name,
    kind:
      region.kind === 'continent' ? 'continent' : region.kind === 'rollup' ? 'roll-up' : 'state',
    eyebrow: continent ? continent.name : region.kind === 'continent' ? 'Continent' : null,
    code: region.code,
    description: fixtureDescription(region),
    updatedAt: null,
    live: false,
    kpis: {
      year: kpis.year,
      illustrative: true,
      items: kpiItems({
        year: kpis.year,
        local: kpis.onlineGgrLocal,
        ggrUsd: kpis.onlineGgrUsd,
        yoy: kpis.yoyGrowth,
        forecastUsd: kpis.forecastGgrUsd,
        forecastYear: kpis.forecastYear,
        perAdultUsd: kpis.ggrPerAdultUsd,
        adults: kpis.adultPopulation,
      }),
    },
    macro: { year: kpis.year, illustrative: true, pairs: macroPairs(macro) },
    annual: fixtureAnnualSets(region),
    fixture: region,
  };
};

/** @desc Which of a region page's sections are live and which are badged placeholders. */
export type RegionDataSummary = { live: string[]; illustrative: string[] };

/**
 * @desc The region page's sections, split by where their data comes from — the same conditions
 * `RegionDetail` renders them on, so the page notice names exactly what is on screen (a market
 * with licensed-entity rows has live market share; one without keeps the badged fixture's). The
 * market updates fragment reports its own content (`fragmentContentOf`): named once it has
 * rendered live or sample items, not while it loads or when there are none.
 */
export function regionDataSummary(
  page: RegionPage,
  editorial: { marketUpdates?: FragmentContent } = {},
): RegionDataSummary {
  const { fixture } = page;
  const live: string[] = [];
  const illustrative: string[] = [];
  const figures = page.live ? live : illustrative;
  figures.push('headline figures', 'macro band');
  if (page.annual.length > 0) figures.push('annual GGR');
  if (page.members) {
    live.push('the markets list');
    if (page.members.some((m) => !m.live)) illustrative.push('markets the warehouse does not hold');
  }
  if (fixture) {
    if (page.kind === 'continent') illustrative.push('the markets list');
    else if (fixture.regulatory) illustrative.push('regulatory snapshot');
    if (fixture.overview) illustrative.push('market overview');
  }
  if (page.marketShare) live.push('market share');
  else if (fixture && fixture.marketShare.length > 0) illustrative.push('market share');
  if (page.monthly) {
    live.push('monthly tracking');
    if (fixture && fixture.monthlyTracking.length > 0) illustrative.push('web-traffic share');
  } else if (fixture && fixture.monthlyTracking.length > 0) illustrative.push('monthly tracking');
  if (page.operatorsPresent) live.push('operators present');
  else if (fixture && fixture.operatorsPresent.length > 0) illustrative.push('operators present');
  live.push('regulatory newsflow');
  if (editorial.marketUpdates === 'live') live.push('market updates');
  if (editorial.marketUpdates === 'sample') illustrative.push('the sample market updates');
  live.push('downloads');
  if (fixture?.survey) illustrative.push('survey data');
  return { live, illustrative };
}

/** @desc "a, b and c". */
export const joinList = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/** @desc The region page notice: what is live, then what is a badged placeholder. */
export const regionNoticeText = ({ live, illustrative }: RegionDataSummary): string =>
  `Live H2 Gambling Capital data: ${joinList(live)}. Placeholders for design review, badged “Illustrative data”: ${joinList(illustrative)}.`;

// ---------------------------------------------------------------------------------------------
// Newsflow and downloads
// ---------------------------------------------------------------------------------------------

const LONG_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const ordinal = (day: number) => {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  return `${day}${['th', 'st', 'nd', 'rd'][day % 10] ?? 'th'}`;
};

/** @desc Figma's long-form news date (C9): "8th September, 2026", in UTC like every portal date. */
export const formatLongDate = (at: Date): string =>
  Number.isNaN(at.getTime())
    ? NO_FIGURE
    : `${ordinal(at.getUTCDate())} ${LONG_MONTHS[at.getUTCMonth()]}, ${at.getUTCFullYear()}`;

/** @desc The "Last updated" month: "September 2026". */
export const formatUpdatedMonth = (at: Date): string =>
  Number.isNaN(at.getTime())
    ? NO_FIGURE
    : `${LONG_MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}`;

/** @desc A catalogue entry's file format as a reader names it: `PDF`, `XLSX`. */
export const fileFormatOf = (entry: { fileName: string; contentType: string }): string => {
  const extension = entry.fileName.includes('.') ? entry.fileName.split('.').pop() : undefined;
  if (extension && extension.length <= 5) return extension.toUpperCase();
  if (entry.contentType.includes('pdf')) return 'PDF';
  if (entry.contentType.includes('spreadsheet') || entry.contentType.includes('excel')) {
    return 'XLSX';
  }
  return 'File';
};
